/*
 * Pay one of your own Wealthoak invoices from your commission wallet.
 * POST /own-invoices/{id}/pay-from-wallet {amount}. The amount is what leaves
 * the wallet; GST and TDS come off it exactly as for a withdrawal, and the net
 * is paid on the invoice. The field starts at the most that is useful (all
 * that's available, or just enough to clear the invoice), which the server
 * works out; limits are checked here in whole paise and again by the server,
 * whose sentences are shown as-is. Once confirmed it goes straight to
 * Wealthoak's billing and can't be cancelled, so the dialog says so.
 */

import { useState } from "react";
import { errorMessage, ownInvoices, type OwnInvoice, type Wallet, type Withdrawal } from "../api/client";
import { Dialog } from "./bits";
import { fmtDate, fromPaise, inr, rateLabel, toPaise, withdrawalBreakdown } from "./format";
import { Receipt } from "./icons";

const AMOUNT = /^\d+(\.\d{0,2})?$/;

export default function PayInvoiceDialog({
  invoice,
  wallet,
  onClose,
  onDone,
}: {
  invoice: OwnInvoice;
  wallet: Wallet;
  onClose: () => void;
  onDone: (w: Withdrawal) => void;
}) {
  // The invoice list may predate a withdrawal made since, so the wallet caps it too.
  const maxPaise = Math.max(0, Math.min(toPaise(invoice.max_from_wallet), toPaise(wallet.available)));
  const most = fromPaise(maxPaise);
  const [amount, setAmount] = useState(most);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clean = amount.replace(/,/g, "").trim();
  const paise = AMOUNT.test(clean) ? toPaise(clean) : NaN;
  const problem = !clean
    ? "Enter an amount."
    : Number.isNaN(paise)
      ? "Use numbers only, with up to 2 decimals."
      : paise <= 0
        ? "Enter an amount greater than zero."
        : paise > maxPaise
          ? `The most you can use on this invoice is ${inr(most)}.`
          : null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (problem) return;
    setBusy(true);
    setError(null);
    try {
      onDone(await ownInvoices.payFromWallet(invoice.id, fromPaise(paise)));
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  const { gst_rate, tds_rate, pan_on_file } = wallet.deductions;
  const split = problem ? null : withdrawalBreakdown(paise, gst_rate, tds_rate);
  const left = split ? toPaise(invoice.balance) - split.net : null;

  return (
    <Dialog
      title={`Pay invoice ${invoice.number} from your wallet`}
      description={`Still to pay: ${inr(invoice.balance)}. Available in your wallet: ${inr(wallet.available)}.`}
      onClose={() => {
        if (!busy) onClose();
      }}
    >
      <form onSubmit={submit} noValidate>
        <div className="field">
          <label className="field__label" htmlFor="pi-amount">From your wallet (₹)</label>
          <div className="wd-amount">
            <span aria-hidden="true">₹</span>
            <input
              id="pi-amount"
              className="input num"
              inputMode="decimal"
              autoComplete="off"
              value={amount}
              aria-invalid={Boolean(problem) && clean !== ""}
              aria-describedby="pi-amount-help"
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ""))}
              data-autofocus
            />
            <button
              type="button"
              className="button button--secondary button--sm"
              onClick={() => setAmount(most)}
              disabled={clean === most}
            >
              Use the most
            </button>
          </div>
          <p id="pi-amount-help" className={problem && clean !== "" ? "field__error" : "field__hint"}>
            {problem && clean !== "" ? problem : `Up to ${inr(most)}.`}
          </p>
        </div>

        <dl className="wd-split" aria-label="What gets paid on the invoice" aria-live="polite">
          <div>
            <dt>From your wallet</dt>
            <dd className="num">{split ? inr(fromPaise(paise)) : "—"}</dd>
          </div>
          <div>
            <dt>GST ({rateLabel(gst_rate)}, included in the amount)</dt>
            <dd className="num">{split ? `− ${inr(fromPaise(split.gst))}` : "—"}</dd>
          </div>
          <div>
            <dt>
              TDS ({rateLabel(tds_rate)} of {split ? inr(fromPaise(split.taxable)) : "the taxable amount"})
            </dt>
            <dd className="num">{split ? `− ${inr(fromPaise(split.tds))}` : "—"}</dd>
          </div>
          <div className="wd-split__net">
            <dt>Paid on the invoice</dt>
            <dd className="num">{split ? inr(fromPaise(split.net)) : "—"}</dd>
          </div>
          <div>
            <dt>Left for you to pay</dt>
            <dd className="num">{left === null ? "—" : left > 0 ? inr(fromPaise(left)) : "Nothing, it's paid"}</dd>
          </div>
        </dl>
        {!pan_on_file && (
          <p className="field__hint wd-split__note">
            No PAN on your profile, so TDS is {rateLabel(tds_rate)}. Ask Wealthoak to add your PAN to bring it down.
          </p>
        )}

        <div className="wd-dest">
          <span className="wd-dest__icon" aria-hidden="true">
            <Receipt size={18} />
          </span>
          <span>
            <small>Paid on</small>
            <strong>Invoice {invoice.number}</strong>
            <span>
              Your Wealthoak subscription{invoice.date ? `, issued ${fmtDate(invoice.date)}` : ""}
            </span>
          </span>
        </div>
        <p className="field__hint pi-final">
          Once you confirm, the payment is recorded on your invoice straight away and can&apos;t be cancelled.
        </p>

        {error && <div className="form-error" role="alert">{error}</div>}

        <div className="form-actions">
          <button type="button" className="button button--secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="button button--primary" disabled={busy || Boolean(problem)}>
            {busy ? "Paying…" : split ? `Pay ${inr(fromPaise(split.net))} on the invoice` : "Pay from wallet"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
